import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug, ContentStatus } from '@jeraisonne/db';
import { useCreateChapter, slugify } from '@/lib/admin/mutations';

export function TutorAdminChaptersNewPage() {
  const navigate = useNavigate();
  const createChapter = useCreateChapter();

  const [subject, setSubject] = useState<SubjectSlug>('math');
  const [level, setLevel] = useState<SchoolLevel>('4e');
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [displayOrder, setDisplayOrder] = useState(1);
  const [status, setStatus] = useState<ContentStatus>('draft');

  function handleTitleChange(val: string) {
    setTitle(val);
    if (!slugTouched) setSlug(slugify(val));
  }

  function handleSlugChange(val: string) {
    setSlugTouched(true);
    setSlug(val);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !slug.trim()) return;
    const result = await createChapter.mutateAsync({
      subject,
      level,
      title: title.trim(),
      slug: slug.trim(),
      description: description.trim() || null,
      display_order: displayOrder,
      status,
    });
    navigate({ to: '/tutor/admin/chapters/$chapterId', params: { chapterId: result.id } });
  }

  return (
    <div className="mx-auto max-w-xl p-4 sm:p-6">
      {/* Back */}
      <Link
        to="/tutor/admin/chapters"
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Chapitres
      </Link>

      <h1 className="font-display text-ink-900 mb-6 text-[22px] font-extrabold">
        Nouveau chapitre
      </h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Subject + Level */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Matière</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value as SubjectSlug)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            >
              {SUBJECTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Niveau</label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value as SchoolLevel)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            >
              {SCHOOL_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Title */}
        <div className="flex flex-col gap-1.5">
          <label className="font-display text-ink-700 text-[12px] font-semibold">Titre</label>
          <input
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="ex. Les forces et leurs effets"
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            required
          />
        </div>

        {/* Slug */}
        <div className="flex flex-col gap-1.5">
          <label className="font-display text-ink-700 text-[12px] font-semibold">
            Slug
            <span className="text-ink-400 ml-1.5 font-normal">(URL)</span>
          </label>
          <input
            type="text"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder="les-forces-et-leurs-effets"
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-mono text-[14px] focus:outline-none focus:ring-2"
            required
          />
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1.5">
          <label className="font-display text-ink-700 text-[12px] font-semibold">
            Description
            <span className="text-ink-400 ml-1.5 font-normal">(optionnel)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Brève description du chapitre..."
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 resize-none rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          />
        </div>

        {/* Display order + Status */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Ordre</label>
            <input
              type="number"
              min={1}
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 1)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Statut</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as ContentStatus)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            >
              <option value="draft">Brouillon</option>
              <option value="published">Publié</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Link to="/tutor/admin/chapters" className="flex-1">
            <Button type="button" variant="outline" className="w-full">
              Annuler
            </Button>
          </Link>
          <Button
            type="submit"
            className="flex-1"
            disabled={createChapter.isPending || !title.trim() || !slug.trim()}
          >
            {createChapter.isPending ? 'Création...' : 'Créer le chapitre'}
          </Button>
        </div>

        {createChapter.isError && (
          <p className="text-accent-600 font-sans text-[13px]">{createChapter.error?.message}</p>
        )}
      </form>
    </div>
  );
}
